import { ActionModal } from '@kcftech/design-system';
import { IAssemblyApplicationResponse } from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { AssemblyParameterFormContent } from 'pages/Settings/AssemblyType/parameter_form/AssemblyParameterFormContent';
import { getAssemblyFieldElementRequest } from 'pages/Settings/AssemblyType/parameter_form/shared';
import { AssemblyParameterFormData } from 'pages/Settings/AssemblyType/parameter_form/types';
import { useAssemblyParameterForm } from 'pages/Settings/AssemblyType/parameter_form/useAssemblyParameterForm';
import { createOrUpdateAssemblyParameter } from 'utils/assembly_application';

export type CreateAssemblyParameterProps = Readonly<{
    application: IAssemblyApplicationResponse;
    onClose: (updatedApplication?: IAssemblyApplicationResponse) => void;
}>;

export function CreateAssemblyParameterModal({
    application,
    onClose,
}: CreateAssemblyParameterProps) {
    const siblingParameters = application.ParametersDefinition?.Data
        ? Object.values(application.ParametersDefinition.Data)
        : [];

    const { showToast } = useNotificationContext();

    const form = useAssemblyParameterForm();

    const {
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
    } = form;

    const { mutate: createParameter, isPending: isSaving } = useMutation({
        mutationFn: async (formData: AssemblyParameterFormData) =>
            createOrUpdateAssemblyParameter(
                application.Id,
                getAssemblyFieldElementRequest(formData)
            ),

        onSuccess: (response) => {
            onClose(response);
            showToast({
                type: 'success',
                content: 'Successfully created assembly application parameter',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to create assembly application parameter',
            }),
    });

    return (
        <ActionModal
            aria-label="Create Assembly Application Parameter"
            title="Create Assembly Application Parameter"
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isSaving)
            }
            onAction={handleSubmit((formData) => createParameter(formData))}
            onClose={() => onClose()}
            actionButtonText="Create"
            closeButtonText="Cancel"
        >
            <AssemblyParameterFormContent
                form={form}
                siblingParameters={siblingParameters}
            />
        </ActionModal>
    );
}
